import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { API, auth } from '../finance/shared';
import { statusMeta } from './bookingStatusMeta';

const WEEKDAYS_SHORT = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];
const ROW_HEIGHT = 42;
const toIso = (d) => d.toISOString().slice(0, 10);
const timeToMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const weekStart = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); return x; };

export default function WeekCalendarView({ venueId, operatingHours, onSelectDay, onSelectBooking }) {
  const [anchor, setAnchor] = useState(weekStart(new Date()));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const hours = operatingHours?.length ? operatingHours.slice().sort((a, b) => a - b) : Array.from({ length: 18 }, (_, i) => 6 + i);
  const rangeStartMin = hours[0] * 60;
  const rangeEndMin = (hours[hours.length - 1] + 1) * 60;

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(anchor); d.setDate(d.getDate() + i); return d; });

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/venues/${venueId}/bookings`, { ...auth(), params: { date_from: toIso(days[0]), date_to: toIso(days[6]) } });
      setBookings(res.data || []);
    } catch { setBookings([]); } finally { setLoading(false); }
  }, [venueId, days[0]?.getTime()]);

  useEffect(() => { load(); }, [load]);

  const todayIso = toIso(new Date());

  return (
    <div data-testid="week-calendar-view">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => setAnchor((a) => { const d = new Date(a); d.setDate(d.getDate() - 7); return d; })} data-testid="week-calendar-prev" className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
        <p className="text-sm font-bold text-slate-800">{days[0].toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' })} — {days[6].toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' })}</p>
        <button onClick={() => setAnchor((a) => { const d = new Date(a); d.setDate(d.getDate() + 7); return d; })} data-testid="week-calendar-next" className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
      </div>

      <div className={`overflow-x-auto no-scrollbar ${loading ? 'opacity-50' : ''}`} data-testid="week-calendar-grid">
        <div className="grid min-w-[640px] grid-cols-[44px_repeat(7,1fr)]">
          <div />
          {days.map((d) => {
            const dIso = toIso(d);
            const isToday = dIso === todayIso;
            return (
              <button key={dIso} onClick={() => onSelectDay(dIso)} data-testid={`week-calendar-day-header-${dIso}`} className={`flex flex-col items-center gap-0.5 rounded-t-lg border-b-2 py-1.5 text-xs font-bold transition-colors hover:bg-slate-50 ${isToday ? 'border-teal-400 text-teal-700' : 'border-slate-200 text-slate-600'}`}>
                <span>{WEEKDAYS_SHORT[d.getDay()]}</span>
                <span>{d.getDate()}</span>
              </button>
            );
          })}

          <div className="relative" style={{ height: hours.length * ROW_HEIGHT, gridColumn: 1 }}>
            {hours.map((h) => (
              <div key={h} className="absolute w-full border-t border-slate-100 text-left text-[10px] text-slate-400" style={{ top: (h * 60 - rangeStartMin) / (rangeEndMin - rangeStartMin) * hours.length * ROW_HEIGHT }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {days.map((d) => {
            const dIso = toIso(d);
            const dayBookings = bookings.filter((b) => b.date === dIso);
            return (
              <div key={dIso} className="relative border-r border-slate-100" style={{ height: hours.length * ROW_HEIGHT }} data-testid={`week-calendar-column-${dIso}`}>
                {hours.map((h) => <div key={h} className="absolute w-full border-t border-slate-100" style={{ top: (h * 60 - rangeStartMin) / (rangeEndMin - rangeStartMin) * hours.length * ROW_HEIGHT }} />)}
                {dayBookings.map((b) => {
                  const startMin = timeToMin(b.start_time);
                  const endMin = timeToMin(b.end_time);
                  const top = Math.max(0, (startMin - rangeStartMin) / (rangeEndMin - rangeStartMin)) * hours.length * ROW_HEIGHT;
                  const height = Math.max(16, ((endMin - startMin) / (rangeEndMin - rangeStartMin)) * hours.length * ROW_HEIGHT - 2);
                  const meta = statusMeta(b.status);
                  return (
                    <button
                      key={b.id} onClick={() => onSelectBooking ? onSelectBooking(b) : onSelectDay(dIso)} data-testid={`week-calendar-booking-${b.id}`}
                      className={`absolute right-0.5 left-0.5 overflow-hidden rounded-md border px-1 py-0.5 text-right text-[10px] font-bold leading-tight ${meta.badge}`}
                      style={{ top, height }}
                    >
                      {b.customer_name || meta.label}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
